import React, { useEffect, useMemo, useState } from "react";
import StatisticsSection from "./StatisticsSection";

import { getGroupsHome } from "../../services/groupServices";
import {
  getAdherentsByGroup,
  getGroupStats,
} from "../../services/dashboardServices";
import { getAllTeamMembers } from "../../services/teamServices";
import { getSettings } from "../../services/settingsServices";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3317";

const DEFAULT_VISUAL = "/img/headercount_new.webp";
const DEFAULT_KANJI = "/img/judo-kanji.webp";

const GROUP_STAT_FIELDS = [
  {
    key: "total",
    label: "Total",
    className: "bg-slate-900 text-white border-slate-900",
  },
  {
    key: "Licencie",
    label: "Licenciés",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  {
    key: "LicencieHorsClub",
    label: "Licenciés hors club",
    className: "bg-teal-50 text-teal-700 border-teal-200",
  },
  {
    key: "inscrit",
    label: "Inscrits",
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  {
    key: "courssessai",
    label: "Cours d’essai",
    className: "bg-orange-50 text-orange-700 border-orange-200",
  },
  {
    key: "Prospect",
    label: "Prospects",
    className: "bg-purple-50 text-purple-700 border-purple-200",
  },
  {
    key: "AnciensLicencieArrêt",
    label: "Anciens arrêtés",
    className: "bg-rose-50 text-rose-700 border-rose-200",
  },
  {
    key: "licenceetarret",
    label: "Licence et arrêt",
    className: "bg-red-50 text-red-700 border-red-200",
  },
];

const resolveVisualUrl = (url = "") => {
  const cleanUrl = String(url || "").trim();

  if (!cleanUrl) return "";

  if (
    /^https?:\/\//i.test(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/img/")) {
    return cleanUrl;
  }

  if (cleanUrl.startsWith("/uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}${cleanUrl}`;
  }

  if (cleanUrl.startsWith("uploads/")) {
    return `${API_BASE.replace(/\/$/, "")}/${cleanUrl}`;
  }

  if (cleanUrl.startsWith("/")) {
    return cleanUrl;
  }

  return `/${cleanUrl}`;
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const isGroupActive = (group) => {
  if (
    group?.is_active === undefined ||
    group?.is_active === null ||
    group?.is_active === ""
  ) {
    return true;
  }

  return (
    group.is_active === true || group.is_active === 1 || group.is_active === "1"
  );
};

const countLicensedFromGroups = (items = []) => {
  if (!Array.isArray(items) || !items.length) return 0;

  return items.reduce((total, item) => {
    return total + toNumber(item?.adherentCount);
  }, 0);
};

const countTeachers = (members = []) => {
  if (!Array.isArray(members)) return 0;

  return members.filter((member) => {
    const haystack = `${member?.categorie || ""} ${member?.fonction || ""}`
      .toLowerCase()
      .trim();

    return (
      haystack.includes("enseign") ||
      haystack.includes("prof") ||
      haystack.includes("assistant") ||
      haystack.includes("bpjeps") ||
      haystack.includes("coach")
    );
  }).length;
};

const normalizeGroupStats = (items = []) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      const normalized = {
        nom: item?.nom || "Groupe sans nom",
      };

      GROUP_STAT_FIELDS.forEach(({ key }) => {
        normalized[key] = toNumber(item?.[key]);
      });

      return normalized;
    })
    .filter((item) => item.total > 0);
};

const aggregateGroupStats = (items = []) => {
  return items.reduce(
    (acc, item) => {
      GROUP_STAT_FIELDS.forEach(({ key }) => {
        acc[key] = toNumber(acc[key]) + toNumber(item?.[key]);
      });

      return acc;
    },
    GROUP_STAT_FIELDS.reduce((acc, { key }) => {
      acc[key] = 0;
      return acc;
    }, {}),
  );
};

function GroupStatsDetails({ groups = [], totals = {}, open, onToggle }) {
  const hasGroups = Array.isArray(groups) && groups.length > 0;

  if (!hasGroups) return null;

  return (
    <div className="mx-auto mt-6 w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="rounded-[2rem] border border-slate-200 bg-white/90 shadow-sm backdrop-blur">
        <button
          type="button"
          onClick={onToggle}
          className="flex w-full items-center justify-between gap-4 rounded-[2rem] px-5 py-4 text-left transition hover:bg-slate-50 sm:px-6"
        >
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-400">
              Répartition
            </p>
            <h3 className="mt-1 text-lg font-bold text-slate-900 sm:text-xl">
              Détail des effectifs par groupe
            </h3>
          </div>

          <span className="shrink-0 rounded-full bg-slate-900 px-4 py-2 text-sm font-bold text-white">
            {open ? "Masquer" : "Voir le détail"}
          </span>
        </button>

        {open && (
          <div className="border-t border-slate-100 px-5 pb-5 sm:px-6 sm:pb-6">
            <div className="mb-5 mt-5 flex flex-wrap gap-2">
              {GROUP_STAT_FIELDS.map(({ key, label, className }) => {
                const value = toNumber(totals?.[key]);
                if (value <= 0) return null;

                return (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold ${className}`}
                  >
                    {label}
                    <span>{value}</span>
                  </span>
                );
              })}
            </div>

            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {groups.map((group) => {
                const visibleStats = GROUP_STAT_FIELDS.filter(({ key }) => {
                  return toNumber(group?.[key]) > 0;
                });

                return (
                  <article
                    key={group.nom}
                    className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
                  >
                    <h4 className="text-sm font-bold leading-snug text-slate-900">
                      {group.nom}
                    </h4>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {visibleStats.map(({ key, label, className }) => (
                        <span
                          key={key}
                          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${className}`}
                        >
                          {label}
                          <span>{toNumber(group?.[key])}</span>
                        </span>
                      ))}
                    </div>
                  </article>
                );
              })}
            </div>

            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              Les chiffres sont calculés depuis les statistiques par groupe. Si
              un adhérent est présent dans plusieurs groupes, il peut être
              compté dans chaque groupe.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function HeaderCount() {
  const [loading, setLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [saisonActive, setSaisonActive] = useState("");

  const [stats, setStats] = useState({
    licencies: 0,
    groupes: 0,
    enseignants: 0,
    inscritsEtCoursEssai: 0,
  });

  const [groupDetails, setGroupDetails] = useState({
    groups: [],
    totals: {},
  });

  const [visuals, setVisuals] = useState({
    visualUrl: DEFAULT_VISUAL,
    kanjiUrl: DEFAULT_KANJI,
  });

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const [groupStatsRes, groupsRes, adherentsRes, teamRes, settingsRes] =
          await Promise.allSettled([
            getGroupStats(),
            getGroupsHome(),
            getAdherentsByGroup(),
            getAllTeamMembers(),
            getSettings(),
          ]);

        if (!mounted) return;

        const groupStatsData =
          groupStatsRes.status === "fulfilled" &&
          Array.isArray(groupStatsRes.value)
            ? groupStatsRes.value
            : [];

        const groupsData =
          groupsRes.status === "fulfilled" && Array.isArray(groupsRes.value)
            ? groupsRes.value
            : [];

        const adherentsByGroupData =
          adherentsRes.status === "fulfilled" &&
          Array.isArray(adherentsRes.value)
            ? adherentsRes.value
            : [];

        const teamData =
          teamRes.status === "fulfilled" && Array.isArray(teamRes.value)
            ? teamRes.value
            : [];

        const settingsArray =
          settingsRes.status === "fulfilled" ? settingsRes.value : [];

        const settings = Array.isArray(settingsArray)
          ? settingsArray[0] || {}
          : settingsArray || {};

        const activeSeason = String(settings?.saison_active || "").trim();
        setSaisonActive(activeSeason);

        const activeGroups = groupsData.filter(isGroupActive);
        const enseignants = countTeachers(teamData);

        const normalizedGroupStats = normalizeGroupStats(groupStatsData);
        const totals = aggregateGroupStats(normalizedGroupStats);

        let licencies = 0;
        let groupes = 0;
        let inscritsEtCoursEssai = 0;

        if (normalizedGroupStats.length > 0) {
          licencies =
            toNumber(totals.Licencie) + toNumber(totals.LicencieHorsClub);

          groupes = normalizedGroupStats.length;

          inscritsEtCoursEssai =
            toNumber(totals.inscrit) + toNumber(totals.courssessai);
        } else {
          licencies = countLicensedFromGroups(adherentsByGroupData);
          groupes = activeGroups.length;

          inscritsEtCoursEssai = toNumber(
            settings?.nb_inscrits_essais ||
              settings?.inscrits_essais ||
              settings?.headercount_inscrits_essais ||
              0,
          );
        }

        setStats({
          licencies,
          groupes,
          enseignants,
          inscritsEtCoursEssai,
        });

        setGroupDetails({
          groups: normalizedGroupStats,
          totals,
        });

        setVisuals({
          visualUrl: resolveVisualUrl(
            settings?.headercount_image ||
              settings?.stats_image ||
              DEFAULT_VISUAL,
          ),
          kanjiUrl: resolveVisualUrl(
            settings?.headercount_kanji ||
              settings?.stats_kanji ||
              DEFAULT_KANJI,
          ),
        });
      } catch (error) {
        console.error("Erreur lors du chargement des statistiques :", error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  const sectionTitle = useMemo(() => {
    if (saisonActive) {
      return `Quelques chiffres saison ${saisonActive}`;
    }

    return "Quelques chiffres";
  }, [saisonActive]);

  return (
    <section id="stats" className="scroll-mt-28">
      <StatisticsSection
        title={sectionTitle}
        loading={loading}
        visualUrl={visuals.visualUrl}
        kanjiUrl={visuals.kanjiUrl}
        licencies={stats.licencies}
        groupes={stats.groupes}
        enseignants={stats.enseignants}
        inscritsEtCoursEssai={stats.inscritsEtCoursEssai}
      />

      {!loading && (
        <GroupStatsDetails
          groups={groupDetails.groups}
          totals={groupDetails.totals}
          open={detailsOpen}
          onToggle={() => setDetailsOpen((current) => !current)}
        />
      )}
    </section>
  );
}
